import { useCallback, useMemo, useState } from "react";
import DialogContent from "@mui/material/DialogContent";
import {
  IEventEngineActionTriggerPayload,
  IEventEngineEvent,
  IFlight,
  IGroundEvent,
} from "@vmosolutions/oc-interfaces";
import { useDispatch } from "react-redux";
import { Grid, Button, useTheme } from "@mui/material";
import { ModalTitle, ModalButton, ModalErrorText } from "../DialogModalComponents"; //ModalMessage
import { EventModalTypes } from "../DialogModalWrapper";
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";
import { CustomFieldsV2 } from "src/components/Custom/CustomFieldsV2";
import { Form, Formik, FormikHelpers, FormikProps } from "formik";
import _ from "lodash";
import { DialogModalStyles } from "../DialogModalWrapper";

/**
 * Modal appears when a custom action contains a formSchema in the actionSettings.
   Allows users to fill in or view those custom fields before submitting action.
 */

interface ICustomActionModalProps {
  isOpen: boolean;
  handleClose: (value: boolean) => void;
  setActiveEventModal: (EventModalTypes: EventModalTypes) => void;
  modalProps: {
    customAction: IEventEngineEvent; // Data pertaining to the custom action.
    items: IFlight[] | IGroundEvent[]; // Events this action applies to.
  };
}

interface ICustomFields {
  customFields: any;
}
type CustomActionForm = FormikProps<ICustomFields>;

export const CustomActionModal = (props: ICustomActionModalProps): JSX.Element => {
  const { isOpen, handleClose, setActiveEventModal, modalProps } = props;
  const dispatch = useDispatch();
  const theme = useTheme();
  const [error, setError] = useState<string | null>(null);
  const title = modalProps?.customAction?.name ?? "";
  const description = modalProps?.customAction?.actionSettings?.userDescription ?? "";

  const modalValues = useMemo(() => {
    return modalProps?.customAction?.actionSettings?.customFields
      ? { customFields: modalProps?.customAction?.actionSettings?.customFields }
      : { customFields: {} };
  }, [modalProps?.customAction?.actionSettings?.customFields]);

  const schema = useMemo(() => {
    try {
      const data = modalProps?.customAction?.actionSettings?.formSchema
        ? JSON.parse(modalProps?.customAction?.actionSettings?.formSchema)
        : null;
      return data;
    } catch (error) {
      setError("Error: Unable to display custom fields, please see administrator for assistance.");
      // console.error(error);
      return null;
    }
  }, [modalProps?.customAction?.actionSettings?.formSchema]);

  const customPath = "customFields";
  const initialCustomValues = useMemo(() => {
    const values: { [key: string]: any } = {};
    if (!schema || !schema.hasOwnProperty("properties") || !modalValues) {
      return values;
    }
    Object.keys(schema.properties).forEach((value: string) => {
      values[value] = schema.properties[value].default ?? schema.properties[value]?.selectorProps?.defaultValue;
    });

    return values;
  }, [schema, modalValues]);

  // Add custom fields to initial values (registers the fields for proper validation)
  const initialValues = useMemo(
    () => _.merge({ [customPath]: initialCustomValues }, { ...modalValues }),
    [modalValues, initialCustomValues, customPath]
  );

  const handleCancel = useCallback((): void => {
    handleClose(false);
    setActiveEventModal(EventModalTypes.None);
  }, [handleClose, setActiveEventModal]);

  const handleCustomAction = useCallback(
    async (values: ICustomFields, helpers: FormikHelpers<ICustomFields>): Promise<void> => {
      const { setSubmitting, setStatus, resetForm } = helpers;
      // console.log("values", values);
      setError(null);
      const { name, appliesTo, _id } = modalProps.customAction;
      const notifications: INotifications = {
        dispatch: dispatch,
        infoMessage: `Sending Request to trigger ${name} action...`,
        successMessage: "Request Received Successfully",
        errorMessage: "Error: Unable to send request, please see administrator for assistance.",
      };
      const payload: IEventEngineActionTriggerPayload = {
        resourceIds: modalProps?.items?.map((item: IFlight | IGroundEvent) => item._id),
        customFields: values?.customFields,
      };
      const response: any =
        modalProps?.items?.length === 1
          ? await AmplifyAPIWrapper.Post(
              `/event/trigger/${appliesTo}/${_id}/${modalProps?.items?.[0]._id}`,
              { customFields: values?.customFields },
              notifications
            )
          : await AmplifyAPIWrapper.Post(`/event/trigger/${appliesTo}/${_id}`, payload, notifications);
      // console.log(response);
      if (response?.error) {
        setError(response?.error);
      } else {
        // Close the modal if there was no error. (users will see snackbar for confirmation of send)
        handleCancel();
      }
      setSubmitting(false);
    },
    [modalProps?.customAction, modalProps?.items, dispatch, handleCancel]
  );

  if (isOpen && title && description && !schema) {
    return (
      <DialogContent sx={DialogModalStyles(theme)}>
        <div className="modal_content">
          <ModalTitle title={description} />
          <ModalErrorText error={error} />
        </div>
      </DialogContent>
    );
  } else if (isOpen && title && description && schema) {
    return (
      <DialogContent sx={DialogModalStyles(theme)}>
        <Formik initialValues={initialValues} enableReinitialize={true} onSubmit={handleCustomAction}>
          {(customActionForm: CustomActionForm) => {
            const { dirty, isValid, isSubmitting, handleReset } = customActionForm;

            return (
              <div className="modal_content">
                <ModalTitle title={description} />
                <ModalErrorText error={error} />
                <Grid className="custom_action_form">
                  <Form>
                    <Grid container direction="column">
                      <CustomFieldsV2 schema={schema} objectPath={customPath} displayHeader={true} />
                      <Grid
                        container
                        wrap="nowrap"
                        style={{ width: "100%" }}
                        alignItems="baseline"
                        justifyContent="flex-end"
                      >
                        <ModalButton label="Close" handler={handleCancel}></ModalButton>
                        <Grid container className="form_footer" style={{ width: "auto" }}>
                          <Button
                            className="btn reset"
                            type="reset"
                            variant="contained"
                            color="secondary"
                            disabled={(!dirty && isValid) || isSubmitting}
                            onClick={handleReset}
                          >
                            Reset
                          </Button>
                          <Button
                            className={"btn save"}
                            variant="contained"
                            color="primary"
                            disabled={isSubmitting || !isValid || !dirty}
                            type="submit"
                          >
                            {title}
                          </Button>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Form>
                </Grid>
              </div>
            );
          }}
        </Formik>
      </DialogContent>
    );
  } else {
    return null;
  }
};
